import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Divider, TextField, Typography } from "@mui/material";
import {
    getActiveResolverConfig,
    listResolverConfigs,
    setActiveResolverConfig as saveActiveResolverConfig,
} from "../../../utils/api";
import type { ResolverConfigSummary } from "../../../utils/api";

export interface ResolverConfigurationSelectProps {
    rubricVersionId: number | null;
    disabled?: boolean;
}

export function ResolverConfigurationSelect({ rubricVersionId, disabled = false }: ResolverConfigurationSelectProps) {
    const [resolverConfigs, setResolverConfigs] = useState<ResolverConfigSummary[]>([]);
    const [resolverConfigsLoading, setResolverConfigsLoading] = useState(false);
    const [activeResolverConfigId, setActiveResolverConfigId] = useState<number | null>(null);
    const [activeResolverConfig, setActiveResolverConfig] = useState<ResolverConfigSummary | null>(null);
    const [resolverConfigSaving, setResolverConfigSaving] = useState(false);
    const [resolverSearchQuery, setResolverSearchQuery] = useState("");
    const resolverSearchFirstMount = useRef(true);
    const resolverSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadResolverConfigsAndActive = useCallback(async (versionId: number) => {
        setResolverConfigsLoading(true);
        try {
            const [listRes, activeRes] = await Promise.all([
                listResolverConfigs({ page: 0, size: 10, sortBy: "name", sortDirection: "asc" }),
                getActiveResolverConfig(versionId).catch(() => null),
            ]);
            setResolverConfigs(listRes?.content ?? []);
            setActiveResolverConfigId(activeRes?.id ?? null);
            setActiveResolverConfig(activeRes ?? null);
        } catch (err) {
            console.error("Failed to load resolver configs", err);
            setResolverConfigs([]);
            setActiveResolverConfigId(null);
            setActiveResolverConfig(null);
        } finally {
            setResolverConfigsLoading(false);
        }
    }, []);

    const loadResolverConfigsSearch = useCallback(async (search: string) => {
        setResolverConfigsLoading(true);
        try {
            const listRes = await listResolverConfigs({
                page: 0,
                size: 10,
                sortBy: "name",
                sortDirection: "asc",
                ...(search.trim() ? { search: search.trim() } : {}),
            });
            setResolverConfigs(listRes?.content ?? []);
        } catch (err) {
            console.error("Failed to search resolver configs", err);
            setResolverConfigs([]);
        } finally {
            setResolverConfigsLoading(false);
        }
    }, []);

    const handleResolverConfigChange = useCallback(
        async (config: ResolverConfigSummary | null) => {
            if (rubricVersionId == null) return;
            const resolverConfigId = config?.id ?? null;
            setResolverConfigSaving(true);
            try {
                await saveActiveResolverConfig({ rubricVersionId, resolverConfigId });
                setActiveResolverConfigId(resolverConfigId);
                setActiveResolverConfig(config);
            } catch (err) {
                console.error("Failed to set active resolver config", err);
            } finally {
                setResolverConfigSaving(false);
            }
        },
        [rubricVersionId]
    );

    useEffect(() => {
        if (rubricVersionId != null) {
            resolverSearchFirstMount.current = true;
            loadResolverConfigsAndActive(rubricVersionId);
            setResolverSearchQuery("");
        } else {
            setResolverConfigs([]);
            setActiveResolverConfigId(null);
            setActiveResolverConfig(null);
        }
    }, [rubricVersionId, loadResolverConfigsAndActive]);

    useEffect(() => {
        if (rubricVersionId == null) return;
        if (resolverSearchTimeoutRef.current) clearTimeout(resolverSearchTimeoutRef.current);
        resolverSearchTimeoutRef.current = setTimeout(() => {
            if (resolverSearchFirstMount.current) {
                resolverSearchFirstMount.current = false;
                return;
            }
            loadResolverConfigsSearch(resolverSearchQuery);
        }, 300);
        return () => {
            if (resolverSearchTimeoutRef.current) clearTimeout(resolverSearchTimeoutRef.current);
        };
    }, [resolverSearchQuery, rubricVersionId, loadResolverConfigsSearch]);

    return (
        <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1">Resolver configuration</Typography>
            <Autocomplete<ResolverConfigSummary, false, false>
                fullWidth
                disabled={disabled || resolverConfigsLoading || resolverConfigSaving || rubricVersionId == null}
                options={resolverConfigs}
                value={
                    resolverConfigs.find((c) => c.id === activeResolverConfigId) ??
                    activeResolverConfig ??
                    null
                }
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onInputChange={(_, value) => setResolverSearchQuery(value)}
                onChange={(_, newValue) => handleResolverConfigChange(newValue)}
                loading={resolverConfigsLoading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Resolver configuration"
                        placeholder="Search or select..."
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            {option.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {option.description}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
            />
        </>
    );
}
